import React, { useState } from 'react';
import { Calendar, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Calendar as CalendarComponent } from './ui/calendar';
import { format } from 'date-fns';
import { hr, enUS } from 'date-fns/locale';
import emailjs from '@emailjs/browser';
import { useLanguage } from '../context/LanguageContext';

// EmailJS Configuration
const EMAILJS_SERVICE_ID = 'service_t4ehl58';
const EMAILJS_TEMPLATE_ID = 'template_ik8nzzd';
const EMAILJS_PUBLIC_KEY = '5RRMCwoC_-zpQCXcs';

const Reservation = () => {
  const { t, language } = useLanguage();
  const dateLocale = language === 'hr' ? hr : enUS;
  
  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  const [guests, setGuests] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!checkIn || !checkOut || !guests || !name || !email || !agreed) {
      return;
    }
    
    setIsSubmitting(true);
    setError(null);
    
    // Prepare email template parameters
    const templateParams = {
      from_name: name,
      from_email: email,
      check_in: format(checkIn, 'dd.MM.yyyy.', { locale: hr }),
      check_out: format(checkOut, 'dd.MM.yyyy.', { locale: hr }),
      guests: guests,
      message: message || (language === 'en' ? 'No additional message' : 'Nema dodatne poruke'),
      to_name: 'Sarajevo City Center Apartment',
    };

    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        EMAILJS_PUBLIC_KEY
      );
      
      setSubmitted(true);
    } catch (err) {
      console.error('EmailJS Error:', err);
      setError(t.reservation.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <section id="rezervacija" className="py-20 bg-stone-50">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="bg-green-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={40} className="text-green-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">{t.reservation.success}</h3>
            <p className="text-gray-600 mb-6">
              {t.reservation.successMessage}
            </p>
            <Button
              onClick={() => {
                setSubmitted(false);
                setCheckIn(null);
                setCheckOut(null);
                setGuests('');
                setName('');
                setEmail('');
                setMessage('');
                setAgreed(false);
              }}
              className="bg-amber-700 hover:bg-amber-800"
            >
              {t.reservation.newInquiry}
            </Button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="rezervacija" className="py-20 bg-stone-50">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <p className="text-amber-700 text-sm font-medium tracking-wider mb-2">{t.reservation.subtitle}</p>
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-gray-900 mb-4">
            {t.reservation.title}
          </h2>
          <p className="text-gray-600">
            {t.reservation.description}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-6 md:p-8" data-testid="reservation-form">
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            {/* Check-in Date */}
            <div className="space-y-2">
              <Label>{t.reservation.checkIn} *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={`w-full justify-start text-left font-normal ${
                      !checkIn && 'text-muted-foreground'
                    }`}
                  >
                    <Calendar className="mr-2 h-4 w-4" />
                    {checkIn ? format(checkIn, 'PPP', { locale: dateLocale }) : t.reservation.selectDate}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-white" align="start">
                  <CalendarComponent
                    mode="single"
                    selected={checkIn}
                    onSelect={setCheckIn}
                    disabled={(date) => date < new Date()}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Check-out Date */}
            <div className="space-y-2">
              <Label>{t.reservation.checkOut} *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={`w-full justify-start text-left font-normal ${
                      !checkOut && 'text-muted-foreground'
                    }`}
                  >
                    <Calendar className="mr-2 h-4 w-4" />
                    {checkOut ? format(checkOut, 'PPP', { locale: dateLocale }) : t.reservation.selectDate}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-white" align="start">
                  <CalendarComponent
                    mode="single"
                    selected={checkOut}
                    onSelect={setCheckOut}
                    disabled={(date) => date < (checkIn || new Date())}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Number of Guests */}
          <div className="space-y-2 mb-6">
            <Label>{t.reservation.guests} *</Label>
            <Select value={guests} onValueChange={setGuests}>
              <SelectTrigger>
                <SelectValue placeholder={t.reservation.selectGuests} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">{t.reservation.person1}</SelectItem>
                <SelectItem value="2">{t.reservation.persons2}</SelectItem>
                <SelectItem value="2+1">{t.reservation.persons2child}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Name */}
          <div className="space-y-2 mb-6">
            <Label>{t.reservation.name} *</Label>
            <Input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t.reservation.namePlaceholder}
              required
            />
          </div>

          {/* Email */}
          <div className="space-y-2 mb-6">
            <Label>{t.reservation.email} *</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t.reservation.emailPlaceholder}
              required
            />
          </div>

          {/* Message */}
          <div className="space-y-2 mb-6">
            <Label>{t.reservation.message}</Label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={t.reservation.messagePlaceholder}
              rows={4}
            />
          </div>

          {/* Agreement */}
          <div className="flex items-start gap-3 mb-8">
            <Checkbox
              id="agreement"
              checked={agreed}
              onCheckedChange={setAgreed}
              className="mt-1"
            />
            <Label htmlFor="agreement" className="text-sm text-gray-600 cursor-pointer">
              {t.reservation.agreement} *
            </Label>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={20} />
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={!checkIn || !checkOut || !guests || !name || !email || !agreed || isSubmitting}
            className="w-full bg-amber-700 hover:bg-amber-800 text-white py-6 text-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="reservation-submit"
          >
            {isSubmitting ? (
              <span className="flex items-center gap-2">
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                {t.reservation.sending}
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Send size={20} />
                {t.reservation.submit}
              </span>
            )}
          </Button>
        </form>
      </div>
    </section>
  );
};

export default Reservation;
